import React, { useEffect, useRef } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useIdioma } from '../../i18n/idioma';
import type { Idioma } from '../../i18n/textos';

/**
 * Pregunta el idioma en la PRIMERA visita, y solo en esa.
 *
 * Quién decide si se muestra es LandingPage (`hayIdiomaGuardado()`); este
 * componente solo se pinta cuando toca.
 *
 * Por qué se pregunta habiendo detección automática: la detección acierta casi
 * siempre —mira el idioma del navegador— pero es invisible. Un contratista con
 * el teléfono en inglés y la cabeza en español no tiene por qué adivinar que
 * existe un selector. Preguntar una vez cuesta un clic; no preguntar puede
 * costar la visita entera.
 *
 * Las dos opciones se escriben CADA UNA EN SU IDIOMA («Español» / «English»),
 * no traducidas al idioma actual. Es la única forma de que quien no lee el
 * idioma que se detectó pueda reconocer el suyo.
 *
 * Elegir cualquiera de las dos cierra la ventana Y guarda la preferencia, así
 * que no vuelve a salir. Escape también: se queda con el idioma que se detectó
 * y lo da por elegido — insistir sería castigar a quien ya está conforme.
 */
const ModalIdioma: React.FC<{ onListo: () => void }> = ({ onListo }) => {
    const { idioma, cambiarIdioma, t } = useIdioma();
    const panelRef = useRef<HTMLDivElement>(null);
    const primeroRef = useRef<HTMLButtonElement>(null);

    /* En el CUERPO del componente. Estuvo dentro del efecto de abajo y eso
       revienta React con «Invalid hook call»: un hook no se llama dentro de
       otro hook. Mismo fallo que tenia ModalAcceso. */
    useBodyScrollLock();

    const elegir = (i: Idioma) => {
        cambiarIdioma(i);
        onListo();
    };

    /* En el cuerpo del componente, NO dentro del efecto de abajo.
       Estuvo ahí dentro: `useBodyScrollLock` es un hook y llamarlo desde el
       callback de un `useEffect` no registra nada —React solo admite hooks
       durante el render—, así que el bloqueo no se aplicaba nunca y la landing
       seguía deslizándose por detrás de esta ventana. */
    useBodyScrollLock();

    useEffect(() => {
        const previo = document.activeElement as HTMLElement | null;
        /* El bloqueo lo lleva `useBodyScrollLock` (abajo), que cuenta capas.
           Guardar y restaurar aquí dejaba la página trabada si esta capa se
           cerraba después de otra que también hubiera bloqueado. */
        primeroRef.current?.focus();

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                // Se da por bueno lo detectado: `cambiarIdioma` lo GUARDA, que es
                // justo lo que hace que no vuelva a preguntar.
                cambiarIdioma(idioma);
                onListo();
                return;
            }
            if (e.key !== 'Tab') return;
            const focusables = panelRef.current?.querySelectorAll<HTMLElement>('button');
            if (!focusables?.length) return;
            const primero = focusables[0];
            const ultimo = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === primero) {
                e.preventDefault();
                ultimo.focus();
            } else if (!e.shiftKey && document.activeElement === ultimo) {
                e.preventDefault();
                primero.focus();
            }
        };

        window.addEventListener('keydown', onKey);

        return () => {
            window.removeEventListener('keydown', onKey);
            previo?.focus?.();
        };
    }, [idioma, cambiarIdioma, onListo]);

    return (
        <div className="rp rp-on-light fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-navy-950/80" aria-hidden="true" />

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="titulo-idioma"
                className="relative w-full max-w-[400px] overflow-hidden rounded-2xl bg-white shadow-s3"
            >
                <div className="flex flex-col items-center bg-navy-850 px-8 py-9">
                    <img src="/logo.webp" alt="" className="h-12 w-auto object-contain" />
                    <p className="mt-3 text-[13px] font-semibold tracking-tight text-brand-400">
                        Raza Pro
                    </p>
                </div>

                <div className="px-8 py-8">
                    {/* El título va en los DOS idiomas, uno debajo del otro. Ponerlo
                        solo en el detectado deja a la mitad de la gente leyendo algo
                        que no entiende justo cuando se le pide que decida. */}
                    <h2
                        id="titulo-idioma"
                        className="text-center text-[19px] font-bold leading-snug tracking-tight text-navy-950"
                    >
                        <span lang="es" className="block">¿En qué idioma prefieres verla?</span>
                        <span lang="en" className="mt-1 block text-acero-500">
                            Which language do you prefer?
                        </span>
                    </h2>

                    <div className="mt-7 grid gap-3">
                        <button
                            ref={primeroRef}
                            type="button"
                            lang="es"
                            onClick={() => elegir('es')}
                            className="w-full rounded-xl bg-navy-850 px-6 py-4 text-[16px] font-semibold text-white transition-colors hover:bg-navy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                        >
                            Español
                        </button>
                        <button
                            type="button"
                            lang="en"
                            onClick={() => elegir('en')}
                            className="w-full rounded-xl border border-acero-300 bg-white px-6 py-4 text-[16px] font-semibold text-navy-950 transition-colors hover:border-navy-850 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                        >
                            English
                        </button>
                    </div>

                    <p className="mt-5 text-center text-[13px] leading-relaxed text-acero-500">
                        {t('lp.idioma.bienvenida.sub')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ModalIdioma;
